import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { ActionCredential } from '@customerio/hydra-ui-api';
import {
  derivePushPlatforms,
  derivePushProviders,
  PLATFORM_LABELS,
  type PushApiError,
  type PushErrorType,
  type PushPlatform,
  type PushPlatformDetails,
  type PushProvider,
} from '@/features/push-credentials';
import { useAnalytics } from '@/services/analytics';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { getChannelCredentialQueryOptions } from '@/services/db/action-credential/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { PUSH_PLATFORM_ROUTES } from './route-paths';
import { appendPushErrors, type PushSettingsError } from './utils/push-errors';

export function usePushBreadcrumbs(workspaceId: number | null) {
  const router = useRouter();
  const params = { workspaceId: workspaceId ?? ('last' as const) };

  useRegisterBreadcrumbs([
    {
      id: 'workspace_settings',
      text: 'Workspace settings',
      href: router.buildLocation({ to: '/workspaces/$workspaceId/settings', params }).href,
    },
    {
      id: 'workspace_settings_push',
      text: 'Push',
      href: router.buildLocation({ to: '/workspaces/$workspaceId/settings/actions/push', params }).href,
    },
  ]);
}

export function usePushPlatformBreadcrumb(workspaceId: number | null, platform: PushPlatform) {
  const router = useRouter();

  useRegisterBreadcrumbs([
    {
      id: `workspace_settings_push_${platform}`,
      text: PLATFORM_LABELS[platform],
      href: router.buildLocation({
        to: PUSH_PLATFORM_ROUTES[platform],
        params: { workspaceId: workspaceId ?? ('last' as const) },
      }).href,
    },
  ]);
}

export type PushPermissions = {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export function usePushPermissions(): PushPermissions {
  const { hasPermission } = usePermissions();

  return {
    canCreate: hasPermission('journeys/push_message_settings/create') === true,
    canEdit: hasPermission('journeys/push_message_settings/edit') === true,
    canDelete: hasPermission('journeys/push_message_settings/delete') === true,
  };
}

export type PushPlatformsState = {
  platforms: PushPlatformDetails[];
  providers: PushProvider[];
  isPending: boolean;
  isFetching: boolean;
};

/**
 * The source of truth for which platforms are configured: every credential write
 * invalidates the environment, so pages never track it themselves.
 */
export function usePushPlatforms(workspaceId?: number): PushPlatformsState {
  const query = useEnvironmentOptional(workspaceId);
  const pushProviders = query.data?.environment?.push_providers;

  return {
    platforms: derivePushPlatforms(pushProviders),
    providers: derivePushProviders(pushProviders),
    isPending: query.data == null,
    isFetching: query.isFetching,
  };
}

export type PushCredentialsState = {
  apns: ActionCredential | null;
  fcm: ActionCredential | null;
  isPending: boolean;
  isFetching: boolean;
};

/** Both slots, because the iOS tab offers the Android FCM key as a way to turn iOS on. */
export function usePushCredentials(workspaceId: number): PushCredentialsState {
  const apns = useQuery(getChannelCredentialQueryOptions(workspaceId, 'push_apns'));
  const fcm = useQuery(getChannelCredentialQueryOptions(workspaceId, 'push_fcm'));

  return {
    apns: apns.data?.credential ?? null,
    fcm: fcm.data?.credential ?? null,
    isPending: apns.data == null || fcm.data == null,
    isFetching: apns.isFetching || fcm.isFetching,
  };
}

export type PushSettingsErrorsState = {
  errors: PushSettingsError[];
  addErrors: (type: PushErrorType, errors: readonly PushApiError[]) => void;
  dismissError: (error: PushSettingsError) => void;
};

/**
 * Held per provider: once the iOS chooser switches, the errors of the provider
 * left behind no longer describe what is on screen.
 */
export function usePushSettingsErrors(): PushSettingsErrorsState {
  const [errors, setErrors] = useState<PushSettingsError[]>([]);

  const addErrors = useCallback((type: PushErrorType, apiErrors: readonly PushApiError[]) => {
    setErrors((current) => appendPushErrors(current, type, apiErrors));
  }, []);

  const dismissError = useCallback((error: PushSettingsError) => {
    setErrors((current) => current.filter((candidate) => candidate.id !== error.id));
  }, []);

  return { errors, addErrors, dismissError };
}

/** The ref keeps the reload after every write from re-reporting the same visit. */
export function usePushSettingsViewedAnalytics(platforms: readonly PushPlatformDetails[], isReady: boolean) {
  const { track } = useAnalytics();
  const hasReported = useRef(false);
  const isIosEnabled = platforms.some((platform) => platform.key === 'ios');
  const isAndroidEnabled = platforms.some((platform) => platform.key === 'android');

  useEffect(() => {
    if (!isReady || hasReported.current) {
      return;
    }
    hasReported.current = true;
    void track('push', 'messageSettingsViewed', {
      'Android Push Status': isAndroidEnabled ? 'Enabled' : 'Disabled',
      'iOS Push Status': isIosEnabled ? 'Enabled' : 'Disabled',
    });
  }, [isReady, isAndroidEnabled, isIosEnabled, track]);
}

/** Reported per provider, so switching the iOS chooser reports the provider now on screen. */
export function usePushConfigurationViewedAnalytics(
  platform: PushPlatform,
  provider: PushProvider,
  isConfigured: boolean,
  isReady: boolean,
) {
  const { track } = useAnalytics();
  const reportedFor = useRef<string | null>(null);
  const key = `${platform}:${provider}`;

  useEffect(() => {
    if (!isReady || reportedFor.current === key) {
      return;
    }
    reportedFor.current = key;
    if (isConfigured) {
      void track('push', 'configurationViewed', { platform: PLATFORM_LABELS[platform] });
    } else {
      void track('push', 'setupStarted', { Platform: PLATFORM_LABELS[platform] });
    }
  }, [isReady, isConfigured, key, platform, track]);
}
